import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  getIdTokenResult,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import {
  UserProfile,
  UserRole,
  AuthState,
  LoginCredentials,
  CreateSubAccountRequest,
  normalizeRole,
  hasMinimumRole,
} from '../models/user.model';
import { environment } from '../../../environments/environment';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  private cacheService = inject(CacheService);

  // Lazy import SessionService to avoid circular dependency
  private sessionService: any = null;

  // Reactive state using signals
  private _authState = signal<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Public computed signals
  readonly authState = this._authState.asReadonly();
  readonly currentUser = computed(() => this._authState().user);
  readonly isAuthenticated = computed(() => this._authState().isAuthenticated);
  readonly isLoading = computed(() => this._authState().isLoading);
  readonly userRole = computed(() => this._authState().user?.role ?? null);
  readonly userCompanies = computed(() => this._authState().user?.companies ?? []);

  // Role-based access helpers (using new role hierarchy)
  readonly isSuperAdmin = computed(
    () => this._authState().user?.role === UserRole.SUPER_ADMIN
  );
  readonly isAdmin = computed(() => {
    const role = this._authState().user?.role;
    return role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN || role === UserRole.CLIENT_ADMIN;
  });
  readonly isOperator = computed(() => {
    const role = this._authState().user?.role;
    return hasMinimumRole(role ?? UserRole.VIEWER, UserRole.OPERATOR);
  });
  readonly isViewer = computed(() => !!this._authState().user);

  // Legacy aliases for backward compatibility
  readonly isClientAdmin = computed(() => this.isAdmin());
  readonly canManageUsers = computed(() => this.isAdmin());

  constructor() {
    this.initAuthListener();
  }

  /**
   * Initialize Firebase auth state listener
   */
  private initAuthListener(): void {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        await this.loadUserProfile(firebaseUser);
        // Start session monitoring on successful auth
        this.startSessionMonitoring();
      } else {
        this._authState.set({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
        });
        // Stop session monitoring on logout
        this.stopSessionMonitoring();
      }
    });
  }

  /**
   * Start session monitoring (lazy load to avoid circular dep)
   */
  private async startSessionMonitoring(): Promise<void> {
    try {
      if (!this.sessionService) {
        const { SessionService } = await import('./session.service');
        this.sessionService = inject(SessionService);
      }
      this.sessionService?.startSessionMonitoring();
    } catch (e) {
      // SessionService not available - session monitoring disabled
    }
  }

  /**
   * Stop session monitoring
   */
  private stopSessionMonitoring(): void {
    this.sessionService?.stopSessionMonitoring();
  }

  /**
   * Load user profile from Firestore and Firebase custom claims
   * Priority: Custom claims for role/companies, Firestore for other profile data
   */
  private async loadUserProfile(firebaseUser: FirebaseUser): Promise<void> {
    try {
      // Get custom claims from Firebase token (role, companies)
      let customClaims: { role?: string; companies?: number[]; createdBy?: string } = {};
      try {
        const idTokenResult = await getIdTokenResult(firebaseUser);
        customClaims = {
          role: idTokenResult.claims['role'] as string,
          companies: idTokenResult.claims['companies'] as number[] || [],
          createdBy: idTokenResult.claims['createdBy'] as string,
        };
        console.log('[AuthService] Custom claims loaded:', {
          role: customClaims.role,
          companiesCount: customClaims.companies?.length
        });
      } catch (claimsError) {
        console.warn('[AuthService] Could not load custom claims:', claimsError);
      }

      const userDoc = doc(this.firestore, `users/${firebaseUser.uid}`);
      const userSnap = await getDoc(userDoc);

      if (userSnap.exists()) {
        const userData = userSnap.data() as UserProfile;

        // Update last login (non-blocking, ignore errors)
        updateDoc(userDoc, {
          lastLoginAt: serverTimestamp(),
        }).catch((err) => {
          console.warn('Could not update lastLoginAt:', err.message);
        });

        // Merge Firestore profile with custom claims (claims take precedence)
        const mergedProfile: UserProfile = {
          ...userData,
          uid: firebaseUser.uid,
          // Custom claims override Firestore values for RBAC
          role: normalizeRole(customClaims.role || userData.role || 'viewer'),
          companies: customClaims.companies || [],
          isTesting: Boolean(userData.isTesting ?? ((customClaims as { isTesting?: boolean }).isTesting === true)),
        };

        this._authState.set({
          user: mergedProfile,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      } else {
        // No Firestore profile - use Firebase Auth data + custom claims
        console.log('[AuthService] No Firestore profile found, using Firebase Auth data + claims');

        const authUserProfile: UserProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || 'unknown@example.com',
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          photoURL: firebaseUser.photoURL || null,
          role: normalizeRole(customClaims.role || 'admin'), // Default to admin if no claims (for dev)
          companies: customClaims.companies || [],
          isTesting: Boolean((customClaims as { isTesting?: boolean }).isTesting === true),
          organizationId: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: customClaims.createdBy || null,
          isActive: true,
          lastLoginAt: new Date(),
        };

        this._authState.set({
          user: authUserProfile,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      }
    } catch (error: any) {
      console.error('[AuthService] Error loading user profile:', error);

      // Check if this is an offline error
      if (error.code === 'unavailable' || error.message?.includes('offline')) {
        // Show environment-specific error message
        if (environment.useEmulators) {
          console.warn('[AuthService] Firestore is offline. Check that emulators are running.');
          console.warn('[AuthService] Expected Firestore emulator at: http://127.0.0.1:8080');

          this._authState.set({
            user: null,
            isLoading: false,
            isAuthenticated: false,
            error: 'Cannot connect to database. Please ensure Firebase emulators are running.',
          });
        } else {
          console.warn('[AuthService] Firestore connection failed. Check your internet connection.');

          this._authState.set({
            user: null,
            isLoading: false,
            isAuthenticated: false,
            error: 'Cannot connect to Firebase. Please check your internet connection and try again.',
          });
        }
      } else {
        this._authState.set({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: `Failed to load user profile: ${error.message || 'Unknown error'}`,
        });
      }
    }
  }

  /**
   * Sign in with email and password
   */
  async login(credentials: LoginCredentials): Promise<boolean> {
    this._authState.update((state) => ({ ...state, isLoading: true, error: null }));

    try {
      // Always use browserLocalPersistence to keep session across page reloads
      // This prevents logout on page refresh, which is standard behavior for dashboards
      await setPersistence(this.auth, browserLocalPersistence);

      await signInWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password
      );
      return true;
    } catch (error: any) {
      const errorMessage = this.getAuthErrorMessage(error.code);
      this._authState.update((state) => ({
        ...state,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }

  /**
   * Sign out current user
   */
  async logout(): Promise<void> {
    try {
      // Clear all cached data
      this.cacheService.clear();

      await signOut(this.auth);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<boolean> {
    try {
      await sendPasswordResetEmail(this.auth, email, {
        url: environment.auth.passwordResetUrl,
        handleCodeInApp: false,
      });
      return true;
    } catch (error) {
      console.error('Error sending password reset:', error);
      return false;
    }
  }

  /**
   * Change the current user's password.
   *
   * Re-authenticates with the current password first (required by Firebase for
   * sensitive operations), then updates to the new password. Returns a
   * user-friendly error message on failure, or null on success.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return 'You must be signed in to change your password.';
    }

    try {
      // Re-authenticate with the current password (required before updatePassword)
      const credential = EmailAuthProvider.credential(user.email ?? '', currentPassword);
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, newPassword);
      return null;
    } catch (error: any) {
      console.error('[AuthService] Error changing password:', error);
      return this.getAuthErrorMessage(error.code);
    }
  }

  /**
   * Create a sub-account (for admins/super admins only)
   */
  async createSubAccount(request: CreateSubAccountRequest): Promise<string | null> {
    if (!this.canManageUsers()) {
      throw new Error('Insufficient permissions to create accounts');
    }

    try {
      // Create Firebase Auth user
      // Note: In production, this should be done via Cloud Functions
      // to avoid sign-out of current user
      const tempPassword = this.generateTempPassword();

      // For production: Use Admin SDK via Cloud Function
      // This is a simplified client-side approach for development
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        request.email,
        tempPassword
      );

      // Create Firestore profile
      const userProfile: UserProfile = {
        uid: userCredential.user.uid,
        email: request.email,
        displayName: request.displayName,
        photoURL: null,
        role: request.role,
        companies: [], // Will be set via backend API with custom claims
        isTesting: environment.deploymentEnv !== 'production',
        organizationId: request.organizationId,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: this.currentUser()?.uid ?? null,
        isActive: true,
        lastLoginAt: null,
      };

      await setDoc(
        doc(this.firestore, `users/${userCredential.user.uid}`),
        userProfile
      );

      // Send password reset so user can set their own password
      await sendPasswordResetEmail(this.auth, request.email, {
        url: environment.auth.passwordResetUrl,
        handleCodeInApp: false,
      });

      return userCredential.user.uid;
    } catch (error: any) {
      console.error('Error creating sub-account:', error);
      throw new Error(this.getAuthErrorMessage(error.code));
    }
  }

  /**
   * Generate a temporary password
   */
  private generateTempPassword(): string {
    return Math.random().toString(36).slice(-12) + 'A1!';
  }

  /**
   * Get Firebase ID token for the current user
   * Returns null if no user is authenticated
   */
  async getIdToken(): Promise<string | null> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return null;
      }
      return await user.getIdToken();
    } catch (error) {
      console.error('[AuthService] Error getting ID token:', error);
      return null;
    }
  }

  /**
   * Get user-friendly error message
   */
  private getAuthErrorMessage(errorCode: string): string {
    const errorMessages: Record<string, string> = {
      'auth/invalid-email': 'Invalid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password is too weak.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
    };

    return errorMessages[errorCode] ?? 'An unexpected error occurred.';
  }
}
